<template>
  <v-data-table
    :headers="displayHeaders"
    :items="items"
    :rows-per-page-items="[10]"
    class="inline-edit"
    hide-actions
  >
    <template slot="items" slot-scope="props">
      <td
        :class="{ 'text-xs-right': header.align === 'right' }"
        :key="header.col"
        v-for="header in displayHeaders"
      >
        <v-text-field
          :value="props.item.name"
          class="readonly"
          disabled
          style="min-width: 260px"
          v-if="header.value === 'name'"
        />
        <v-text-field
          :value="props.item.subTotal"
          @input="(v) => changeSubTotal(props.item, v)"
          @keydown="
            (e) => formatNumber(e, props.item.currencyCode === 'JPY' ? 0 : 2)
          "
          placeholder="金額"
          type="number"
          v-if="header.value === 'subTotal'"
        />

        <v-text-field
          :value="
            format1000(
              props.item.originalSubTotal,
              props.item.originalSubTotalNumberFocus
            )
          "
          @blur="(e) => originalSubTotalNumberBlur(props.item)"
          @focus="(e) => numberFocus(props.item, 'originalSubTotal')"
          @input="(v) => numberInput(props.item, v, 'originalSubTotal')"
          @keydown="(e) => onlyNumber(e)"
          placeholder="金額"
          v-if="header.value === 'formatOriginSubTotal'"
        />

        <v-text-field
          :value="
            format1000(props.item.subTotal, props.item.subTotalNumberFocus)
          "
          :rules="
            props.item.originalSubTotal
              ? [(v) => !!v || `金額 is required`]
              : []
          "
          @blur="
            (e) =>
              numberBlur(props.item, 'subTotal', 'jpySubTotal', 'jpyTaxTotal')
          "
          @focus="(e) => numberFocus(props.item, 'subTotal')"
          @input="(v) => numberInput(props.item, v, 'subTotal')"
          @keydown="(e) => onlyNumber(e)"
          placeholder="金額"
          v-if="header.value === 'formatSubTotal'"
        />

        <v-text-field
          :value="
            format1000(
              props.item.paymentSubTotal,
              props.item.paymentSubTotalNumberFocus
            )
          "
          :rules="
            props.item.originalSubTotal
              ? [(v) => !!v || `金額 is required`]
              : []
          "
          @blur="
            (e) =>
              numberBlur(
                props.item,
                'paymentSubTotal',
                'paymentJpySubTotal',
                'paymentJpyTaxTotal'
              )
          "
          @focus="(e) => numberFocus(props.item, 'paymentSubTotal')"
          @input="(v) => numberInput(props.item, v, 'paymentSubTotal')"
          @keydown="(e) => onlyNumber(e)"
          placeholder="金額"
          v-if="header.value === 'formatPaymentSubTotal'"
        />

        <v-text-field
          :value="props.item.subTotal"
          @input="(v) => changeSubTotal(props.item, v)"
          @keydown="
            (e) =>
              formatPositiveNumber(e, props.item.currencyCode === 'JPY' ? 0 : 2)
          "
          placeholder="金額"
          type="number"
          v-if="header.value === 'nonNegativeSubTotal'"
        />
        <v-text-field
          :disabled="defaultDisabled"
          :rules="[(v) => v != null || `金额 is required`]"
          :value="props.item.subTotal"
          @input="(v) => changeSubTotal(props.item, v)"
          @keydown="
            (e) =>
              formatPositiveNumber(e, props.item.currencyCode === 'JPY' ? 0 : 2)
          "
          placeholder="金額"
          type="number"
          v-if="header.value === 'ccfeeSubtotal'"
        />

        <remote-autocomplete
          :placeholder="$t('app.labels.currency')"
          :rules="props.item.subTotal ? [(v) => !!v || `通貨 is required`] : []"
          :value="props.item.currencyCode"
          @input="(v) => changeCurrency(props.item, v)"
          item-text="iso3code"
          item-value="code"
          returnObject
          style="width: 120px"
          url="currencies/all"
          v-if="header.value === 'currencyCode'"
        />
        <v-text-field
          :disabled="true"
          v-model="props.item.currencyCode"
          v-if="header.value === 'jpyCurrencyCode'"
        />
        <v-text-field
          :disabled="true"
          v-model="props.item.ttbRate"
          v-if="header.value === 'ttbRate'"
        />
        <v-text-field
          :disabled="true"
          :value="calcTtbUsdAmount(props.item)"
          v-if="header.value === 'ttbUsdAmount'"
        />
        <v-text-field
          :value="props.item.currencyRate"
          class="readonly"
          disabled
          v-if="header.value === 'currencyRate'"
        />
        <v-text-field
          :value="props.item.jpySubTotal"
          @input="(v) => changeJpySubTotal(props.item, v)"
          @keydown="(e) => formatNumber(e, 0)"
          placeholder="換算金額"
          type="number"
          v-if="header.value === 'jpySubTotal'"
        />
        <remote-select1
          :disabled="consumptionTaxDisabled(props.item)"
          :value="props.item.taxTypeId"
          @input="(v) => changeConsumptionTax(props.item, v)"
          @loadData="fetchTaxes"
          :rules="
            props.item.subTotal ? [(v) => !!v || `税区分 is required`] : []
          "
          clearable
          item-text="taxTypeName"
          item-value="taxTypeId"
          placeholder="税区分"
          returnObject
          style="width: 120px"
          url="consumption-tax-rates/all"
          v-if="header.value === 'consumptionTaxId'"
        />

        <v-text-field
          :value="Math.round(props.item.jpySubTotal || 0)"
          class="readonly"
          disabled
          v-if="header.value === 'jpySubTotalNoTax'"
        />
        <v-text-field
          :value="Math.round(props.item.jpyTaxTotal || 0)"
          class="readonly"
          disabled
          v-if="header.value === 'jpyTaxTotal'"
        />
        <v-text-field
          :value="props.item.comment"
          @input="(v) => changeComment(props.item, v)"
          clearable
          placeholder="コメント"
          v-if="header.value === 'comment'"
        />
        <comment-input
          :value="props.item.comment"
          @input="(v) => changeComment(props.item, v)"
          :items="['Warehouse Fee', 'High way Fee']"
          v-if="header.value === 'autoComment'"
        />
        <v-text-field
          :disabled="defaultDisabled"
          :value="props.item.comment"
          @input="(v) => changeComment(props.item, v)"
          clearable
          placeholder="コメント"
          v-if="header.value === 'ccfeeComment'"
        />
        <remote-autocomplete
          :value="props.item.supplierId"
          @input="(v) => changeSupplier(props.item, v)"
          clearable
          placeholder="请输入支払先"
          url="companies/supplier"
          v-if="header.value === 'supplierId'"
        />
        <remote-autocomplete
          :disabled="defaultDisabled"
          :rules="
            props.item.subTotal || props.item.paymentSubTotal
              ? [(v) => !!v || `支払先 is required`]
              : []
          "
          :value="props.item.supplierId"
          @input="(v) => changeSupplier(props.item, v)"
          clearable
          placeholder="请输入支払先"
          url="companies/supplier"
          v-if="header.value === 'supplierIdWithRequired'"
        />
        <remote-autocomplete
          :disabled="true"
          :filter="(i) => true"
          :init="
            (p) =>
              p.value === props.item.supplierId || p.value === defaultSupplier
          "
          :value="props.item.supplierId"
          @input="(v) => changeSupplier(props.item, v)"
          clearable
          placeholder="请输入支払先"
          url="companies/supplier"
          v-if="header.value === 'supplierIdFixed'"
        />
        <remote-autocomplete
          :disabled="true"
          :filter="(i) => true"
          :init="
            (p) =>
              (props.item.isNameWithTax &&
                p.taxTypeId === 'TAX_DISTINCTION-TAX_INCLUDE') ||
              (props.item.isNameWithFreeTax &&
                p.taxTypeId === 'TAX_DISTINCTION-DUTY_FREE')
          "
          :value="props.item.taxTypeId"
          @input="(v) => changeConsumptionTax(props.item, v)"
          clearable
          item-text="taxTypeName"
          item-value="taxTypeId"
          placeholder="税区分"
          returnObject
          style="width: 120px"
          url="consumption-tax-rates/all"
          v-if="header.value === 'fixedConsumptionTaxId'"
        />

        <remote-autocomplete
          v-model="props.item.providerId"
          :rules="
            props.item.originalSubTotal ||
            props.item.subTotal ||
            props.item.paymentSubTotal
              ? [(v) => !!v || `諸掛請求先 is required`]
              : []
          "
          clearable
          placeholder="请输入諸掛請求先"
          url="overseas-providers/kv"
          v-if="header.value === 'providerId'"
        />
      </td>
    </template>
  </v-data-table>
</template>

<script>
export default {
  props: [
    'shipment',
    'scope',
    'supplierId',
    'columns',
    'columnHeaders',
    'exchangeRateScope',
    'exchangeRateDate',
    'taxDate',
    'taxDateType',
    'defaultSupplier',
    'defaultDisabled',
  ],
  data() {
    return {
      taxes: [],
      items: [],
      headers: [
        {
          text: '費用項目',
          width: '200px',
          value: 'name',
          sortable: false,
          col: '0',
        },
        {
          text: '金額',
          value: 'subTotal',
          width: '200px',
          sortable: false,
          align: 'right',
          col: '1',
        },
        {
          text: '通貨',
          value: 'currencyCode',
          width: '200px',
          sortable: false,
          col: '2',
        },
        {
          text: 'レート',
          value: 'currencyRate',
          width: '200px',
          sortable: false,
          align: 'right',
          col: '3',
        },
        {
          text: '換算金額',
          value: 'jpySubTotal',
          width: '200px',
          sortable: false,
          align: 'right',
          col: '4',
        },
        {
          text: '税区分',
          value: 'consumptionTaxId',
          width: '200px',
          sortable: false,
          col: '5',
        },
        {
          text: '円換算税抜金額',
          value: 'jpySubTotalNoTax',
          width: '200px',
          sortable: false,
          align: 'right',
          col: '6',
        },
        {
          text: '円換算税金金額',
          value: 'jpyTaxTotal',
          width: '200px',
          sortable: false,
          align: 'right',
          col: '7',
        },
        {
          text: 'コメント',
          width: '600px',
          value: 'comment',
          sortable: false,
          col: '8',
        },
        {
          text: '支払先',
          value: 'supplierId',
          width: '600px',
          sortable: false,
          col: '9',
        },
        {
          text: '税抜きJPY小計',
          value: 'jpySubTotalNoTax',
          width: '200px',
          sortable: false,
          align: 'right',
          col: '10',
        },
        {
          text: '税金JPY小計',
          value: 'jpyTaxTotal',
          width: '200px',
          sortable: false,
          align: 'right',
          col: '11',
        },
        {
          text: '输入費用項目',
          value: 'name',
          sortable: false,
          col: '12',
        },
        {
          text: '外貨小計',
          value: 'subTotal',
          width: '200px',
          sortable: false,
          align: 'right',
          col: '13',
        },
        {
          text: '円価',
          value: 'jpySubTotal',
          sortable: false,
          width: '100px',
          align: 'right',
          col: '14',
        },
        {
          text: '税区分',
          value: 'fixedConsumptionTaxId',
          sortable: false,
          align: 'left',
          col: '15',
        },
        {
          text: '支払先',
          value: 'supplierIdWithRequired',
          width: '600px',
          sortable: false,
          col: '16',
        },
        {
          text: '金額',
          value: 'nonNegativeSubTotal',
          width: '100px',
          sortable: false,
          col: '17',
        },
        {
          text: '支払先',
          value: 'supplierIdFixed',
          width: '600px',
          sortable: false,
          col: '18',
        },
        {
          text: '金額*',
          value: 'ccfeeSubtotal',
          width: '100px',
          sortable: false,
          col: '19',
        },
        {
          text: 'コメント',
          width: '600px',
          value: 'ccfeeComment',
          sortable: false,
          col: '20',
        },
        {
          text: '金額',
          value: 'formatOriginSubTotal',
          width: '200px',
          sortable: false,
          align: 'right',
          col: '21',
        },
        {
          text: '請求調整後金額',
          value: 'formatSubTotal',
          width: '200px',
          sortable: false,
          align: 'right',
          col: '22',
        },
        {
          text: '支払調整後金額',
          value: 'formatPaymentSubTotal',
          width: '200px',
          sortable: false,
          align: 'right',
          col: '23',
        },
        {
          text: '諸掛請求先',
          value: 'providerId',
          sortable: false,
          col: '24',
          width: 200,
        },
        {
          text: 'コメント',
          value: 'autoComment',
          sortable: false,
          col: '25',
          width: 200,
        },
        {
          text: '通貨',
          value: 'jpyCurrencyCode',
          width: '200px',
          sortable: false,
          col: '26',
        },
        {
          text: 'TTBレート',
          value: 'ttbRate',
          width: '200px',
          sortable: false,
          col: '27',
        },
        {
          text: '换算USD金额',
          value: 'ttbUsdAmount',
          width: '200px',
          sortable: false,
          col: '28',
        },
      ],
    }
  },
  mounted() {
    this.loadChargeItems()
  },
  computed: {
    taxTypeFreightCalc() {
      return this.taxDateType === 'freightCalc'
    },
    displayHeaders() {
      if (this.columns) {
        return this.columns.split(',').map((c) => {
          let header = this.headers.find((h) => h.col === c)
          if (header) {
            header.text =
              (this.columnHeaders && this.columnHeaders[c]) || header.text
          }
          return header
        })
      } else {
        return this.headers
      }
    },
  },
  watch: {
    shipment() {
      this.loadChargeItems()
    },
    'shipment.transportModeId': function () {
      this.loadChargeItems()
    },
    'shipment.stackTypeId': function () {
      this.loadChargeItems()
    },
    'shipment.deliveryModelId': function () {
      this.loadChargeItems()
    },
  },
  methods: {
    calcTtbUsdAmount(item) {
      if (item.ttbRate) {
        return Math.round(((item.jpySubTotal || 0) * 100) / item.ttbRate) / 100
      }
      return null
    },
    numberFocus(item, key = 'subTotal') {
      this.$set(item, `${key}NumberFocus`, true)
      this.numberInput(item, item[key] + '', key)
    },
    originalSubTotalNumberBlur(
      item,
      usdKey = 'originalSubTotal',
      jpyKey = 'originalJpySubTotal',
      taxKey = 'originalJpyTaxTotal'
    ) {
      this.numberBlur(item, usdKey, jpyKey, taxKey)

      if (!item.subTotal && item.subTotal !== 0) {
        this.changeSubTotal(
          item,
          this.replaceJapanese(item[`originalSubTotalNumberInput`]),
          'subTotal',
          'jpySubTotal',
          'jpyTaxTotal'
        )
      }
      if (!item.paymentSubTotal && item.paymentSubTotal !== 0) {
        this.changeSubTotal(
          item,
          this.replaceJapanese(item[`originalSubTotalNumberInput`]),
          'paymentSubTotal',
          'paymentJpySubTotal',
          'paymentJpyTaxTotal'
        )
      }
    },
    numberBlur(
      item,
      usdKey = 'subTotal',
      jpyKey = 'jpySubTotal',
      taxKey = 'jpyTaxTotal'
    ) {
      this.$set(item, `${usdKey}NumberFocus`, false)
      this.changeSubTotal(
        item,
        this.replaceJapanese(item[`${usdKey}NumberInput`]),
        usdKey,
        jpyKey,
        taxKey
      )
      console.log(`values: ${item[usdKey]},${item[jpyKey]},${item[taxKey]}`)
    },
    numberInput(item, v, key) {
      this.$set(item, `${key}NumberInput`, v)
    },
    consumptionTaxDisabled(item) {
      return item.currencyCode !== 'JPY'
    },
    fetchTaxes(data) {
      this.taxes = data
    },
    format1000(num, numberFocus) {
      // console.log('num', numberFocus, num, num === 0)
      if (num === 0) {
        return '0'
      }
      if (num == null || num == '') {
        return ''
      }
      if (numberFocus) {
        return num
      }
      return parseFloat(num).toLocaleString('en-US')
      // return num
    },
    replaceJapanese(num) {
      if (num == null || num == '') {
        return ''
      }
      return num
        .replace(/１/g, '1')
        .replace(/２/g, '2')
        .replace(/３/g, '3')
        .replace(/４/g, '4')
        .replace(/５/g, '5')
        .replace(/６/g, '6')
        .replace(/７/g, '7')
        .replace(/８/g, '8')
        .replace(/９/g, '9')
        .replace(/０/g, '0')
    },
    onlyNumber(e) {
      // return true;
      if (e.key >= '0' && e.key <= '9') {
        return true
      }
      if ([8, 9, 46].indexOf(e.keyCode) !== -1) {
        return true
      }
      e.preventDefault()
    },
    formatPositiveNumber(e, precision) {
      var selObj = window.getSelection()
      if (
        selObj &&
        selObj.anchorNode &&
        selObj.anchorNode.childNodes[0] === e.target &&
        selObj.type === 'Range'
      ) {
        return false
      }
      if (precision === 0 && e.key === '.') {
        e.preventDefault()
      } else if (e.key === '.' && e.target.value.indexOf('.') !== -1) {
        e.preventDefault()
      } else if (
        e.key >= '0' &&
        e.key <= '9' &&
        e.target.value.indexOf('.') !== -1 &&
        e.target.value.length - e.target.value.indexOf('.') > precision
      ) {
        e.preventDefault()
      } else if (e.key === '-') {
        e.preventDefault()
      }
    },
    formatNumber(e, precision) {
      var selObj = window.getSelection()
      if (
        selObj &&
        selObj.anchorNode &&
        selObj.anchorNode.childNodes[0] === e.target &&
        selObj.type === 'Range'
      ) {
        return false
      }
      if (precision === 0 && e.key === '.') {
        e.preventDefault()
      } else if (e.key === '.' && e.target.value.indexOf('.') !== -1) {
        e.preventDefault()
      } else if (
        e.key >= '0' &&
        e.key <= '9' &&
        e.target.value.indexOf('.') !== -1 &&
        e.target.value.length - e.target.value.indexOf('.') > precision
      ) {
        e.preventDefault()
      }
    },
    show(item) {
      return (
        (this.$isAir(this.shipment.transportModeId) &&
          this.$isAir(item.transportModeId)) ||
        (this.$isSea(this.shipment.transportModeId) &&
          this.$isSea(item.transportModeId) &&
          this.shipment.stackTypeId === 'STACK_TYPE-FCL' &&
          item.stackType === 'FCL') ||
        (this.$isSea(this.shipment.transportModeId) &&
          this.$isSea(item.transportModeId) &&
          this.shipment.stackTypeId === 'STACK_TYPE-LCL' &&
          item.stackType === 'LCL') ||
        (!item.transportModeId && !item.stackType)
      )
    },
    loadChargeItems() {
      this.$http.get('/charge-items?size=1000&sort=seq,asc').then((res) => {
        this.clearChargesByChargeItem(res.data.content)
        this.items = res.data.content
          .filter((i) => i.scopes && i.scopes.indexOf(this.scope) !== -1)
          .filter((i) => i.isAdditionalCharge)
          .filter((i) => this.show(i))
          .map((i) => {
            let charge = this.findCharge(i) || {
              currencyCode: 'JPY',
              currencyRate: 1,
              supplierId: this.supplierId,
            }
            return { ...i, ...charge, id: i.id }
          })
      })
    },
    clearChargesByChargeItem(items) {
      let excludes = items
        .filter((i) => i.scopes && i.scopes.indexOf(this.scope) !== -1)
        .filter((i) => i.isAdditionalCharge)
        .filter((i) => !this.show(i))
        .map((i) => i.id)
      this.shipment.charges =
        this.shipment.charges &&
        this.shipment.charges.filter(
          (c) => excludes.indexOf(c.chargeItemId) === -1
        )
    },
    calcTax(item, jpyKey = 'jpySubTotal', taxKey = 'jpyTaxTotal') {
      this.$set(
        item,
        taxKey,
        Math.round((item[jpyKey] || 0) * (item.consumptionTaxRate || 0))
      )
    },
    calcJpySubTotal(
      item,
      usdKey = 'subTotal',
      jpyKey = 'jpySubTotal',
      taxKey = 'jpyTaxTotal'
    ) {
      this.$set(
        item,
        jpyKey,
        Math.round((item[usdKey] || 0) * (item.currencyRate || 1))
      )
      this.calcTax(item, jpyKey, taxKey)
    },
    calcSubTotal(item) {
      let result = (item.jpySubTotal || 0) / (item.currencyRate || 1)
      if (item.currencyCode === 'JPY') {
        result = item.jpySubTotal || 0
      } else {
        result = Math.round(result * 100) / 100
      }
      this.$set(item, 'subTotal', result)
    },
    updateCharge(item) {
      this.shipment.charges = this.shipment.charges || []
      let charge = this.findCharge(item)
      if (!charge) {
        charge = {}
        this.shipment.charges = [...this.shipment.charges, charge]
      }
      charge.chargeItemId = item.id
      charge.chargeItemName = item.name
      charge.chargeItemCode = item.code
      charge.shipmentId = this.shipment.id
      charge.subTotal = item.subTotal
      charge.price = item.price
      charge.qty = item.qty
      charge.currencyRate = item.currencyRate
      charge.currencyCode = item.currencyCode
      charge.jpySubTotal = item.jpySubTotal
      charge.jpyTaxTotal = item.jpyTaxTotal

      charge.originalSubTotal = item.originalSubTotal
      charge.originalJpySubTotal = item.originalJpySubTotal

      charge.paymentSubTotal = item.paymentSubTotal
      charge.paymentJpySubTotal = item.paymentJpySubTotal
      charge.paymentJpyTaxTotal = item.paymentJpyTaxTotal

      charge.consumptionTaxRate = item.consumptionTaxRate
      charge.consumptionTaxId = item.consumptionTaxId
      charge.taxTypeId = item.taxTypeId

      charge.comment = item.comment
      charge.supplierId = item.supplierId
    },
    findCharge(item) {
      let charge =
        this.shipment &&
        this.shipment.charges &&
        this.shipment.charges.find((c) => c.chargeItemId === item.id)
      return charge
    },
    changeAmount(item, field, value) {
      let amount = parseFloat(value)
      if (isNaN(amount)) amount = null
      this.$set(item, field, amount)
    },
    changeSubTotal(
      item,
      value,
      usdKey = 'subTotal',
      jpyKey = 'jpySubTotal',
      taxKey = 'jpyTaxTotal'
    ) {
      this.changeAmount(item, usdKey, value)
      this.calcJpySubTotal(item, usdKey, jpyKey, taxKey)
      this.updateCharge(item)
    },
    changeJpySubTotal(item, value) {
      this.changeAmount(item, 'jpySubTotal', value)
      this.calcSubTotal(item)
      this.calcTax(item)
      this.updateCharge(item)
    },
    changeCurrency(item, currency) {
      this.$set(item, 'currencyCode', currency && currency.code)
      this.$set(
        item,
        'currencyRate',
        currency &&
          this.$getCurrencyRate(
            currency,
            this.exchangeRateScope,
            this.exchangeRateDate
          )
      )
      if (item.currencyCode !== 'JPY') {
        this.changeConsumptionTax(
          item,
          this.taxes.filter((i) => i.taxTypeCode === 'DUTY_FREE')[0]
        )
      }
      if (item.currencyCode === 'JPY' && item.subTotal) {
        item.subTotal = Math.round(item.subTotal)
      }
      if (item.subTotal) {
        this.calcJpySubTotal(item)
      } else {
        this.calcSubTotal(item)
      }
      this.updateCharge(item)
    },
    changeConsumptionTax(item, tax) {
      this.$set(item, 'taxTypeId', tax && tax.taxTypeId)
      let consumptionTax = this.findConsumptionTax(tax)
      this.$set(item, 'consumptionTaxId', consumptionTax.id)
      this.$set(item, 'consumptionTaxRate', consumptionTax.rate)
      this.calcTax(item)
      this.updateCharge(item)
    },
    findConsumptionTax(item) {
      if (item && item.rates) {
        let arr = item.rates
          .filter((i) => {
            let startDate =
              (this.taxTypeFreightCalc
                ? i.freightCalcStartDate
                : i.startDate) || '0000-00-00'
            return startDate.localeCompare(this.taxDate) <= 0
          })
          .filter((i) => {
            let endDate =
              (this.taxTypeFreightCalc ? i.freightCalcEndDate : i.endDate) ||
              '9999-99-99'
            return endDate.localeCompare(this.taxDate) >= 0
          })
        if (arr.length > 0) {
          //console.log('rate:', arr[0].rate)
          return arr[0]
        }
      }
      // console.log('rate:', 0)
      return {}
    },
    changeComment(item, comment) {
      this.$set(item, 'comment', comment)
      this.updateCharge(item)
    },
    changeSupplier(item, supplierId) {
      this.$set(item, 'supplierId', supplierId)
      this.updateCharge(item)
    },
  },
}
</script>
<style scoped>
.readonly /deep/ input {
  color: #333 !important;
}
</style>

<style>
.text-xs-right input {
  text-align: right;
}
</style>
